import { FILTER_CASES } from "./FILTER_CASES.js";

/**
 * Helper class for creating filter query strings for different software and service types.
 * @memberof vef.map.filters
 */
const FilterString = {

    /**    
     * Regular Expression
     */
    regex: {
        namespace: /%NAMESPACE%/,
        layer: /%LAYER%/,
        layers: /%LAYERS%/,
        syntax: /%SYNTAX%/,
        filter: /%FILTER%/,
        filters: /%FILTERS%/,
        column: /%COLUMN%/g,
        value: /%VALUE%/,
        min: /%MIN%/,
        max: /%MAX%/,
        //		minmax: 	/%MIN%|%MAX%/g
    },

    /**
     * Get the filter query for a service based on the filter config for a specific layer
     * 
     * @param {Layer} layer 
     * @param {object[]} filters 
     * 
     * @returns {string}
     */
    getQuery: function (filters, software, serviceType, filterType) {
        const query = {};
        const filterCase = FilterString.getFilterCase(software, serviceType, filterType);
        const prefix = FilterString.getQueryPrefix(software, serviceType, filterType);

        if (filterCase) {
            switch (filterCase.format) {
                case "xml":
                    query[prefix] = FilterString.getQueryAsXML(filters, filterCase);
                    break;
                case "json":
                    query[prefix] = FilterString.getQueryAsJSON(filters, filterCase);
                    break;
            }
        }

        return query;
    },

    /**
     * get the Filter Case for a specific filter type
     * @param {string} software 
     * @param {string} serviceType 
     * @param {string} filterType 
     */
    getFilterCase: function (software, serviceType, filterType) {
        software = software.toLowerCase();
        serviceType = serviceType.toLowerCase();
        filterType = filterType.toLowerCase();
        if (FILTER_CASES[software] && FILTER_CASES[software][serviceType] && FILTER_CASES[software][serviceType][filterType]) {
            return FILTER_CASES[software][serviceType][filterType];
        }
        return null;
    },

    /**
     * get the query prefix for a specific filter type
     * @param {string} software 
     * @param {string} serviceType 
     * @param {string} filterType 
     */
    getQueryPrefix: function (software, serviceType, filterType) {
        const filterCase = this.getFilterCase(software, serviceType, filterType);
        return (filterCase && filterCase.prefix) ? filterCase.prefix : null;
    },

    /**
     * Creates the Filter Query based on filter, uniqueId of the layer
     * and the provider and type of service
     * 
     * @param {object[]} filters 
     * @param {object} currentCase 
     */
    getQueryAsJSON(filters, currentCase) {
        const that = this;
        let queryString = '';
        let previousFilter = false;

        const setQueryString = function (filter) {
            let filterValues = that.getValuesForJSON(
                currentCase,
                filter.column,
                filter.values
            );
            if (previousFilter) {
                queryString += currentCase.and.replace(
                    that.regex.filter, filterValues
                );
            } else {
                queryString += filterValues;
                previousFilter = true;
            }
        }

        for (let i = 0; i < filters.length; i++) {
            setQueryString(filters[i], queryString)
        }

        return queryString;
    },

    /**
     * Helper function for getQueryAsJSON
     * 
     * @param {string} column 
     * @param {array} values
     */
    getValuesForJSON: function (currentCase, column, values) {
        const that = this;
        let previousValue = false;

        function between(column, min, max, type, condition) {
            return that.setOperator(
                'or',
                that.setBetweenString(column, min, max, type, currentCase),
                condition,
                currentCase
            );
        };

        function equal(column, value, type, condition) {
            return that.setOperator(
                'or',
                that.setEqualString(column, value, type, currentCase),
                condition,
                currentCase
            );
        };

        function forEachValue(value) {
            const type = value[0];

            if (type === "bt") {
                /**
                 * Time Filter (Single Column)
                 * type [bt] 		=> value[0]
                 * column			=> _column
                 * min 			    => value[1]
                 * max				=> value[2]
                 */
                return between(column, value[1], value[2], type, previousValue);
            }
            else if (value.length === 3 && !column) {
                /** 
                 * Multiple (different) Columns
                 * type [eq|gt|lt] 	=> value[0]
                 * column			=> value[1]
                 * value		    => value[2]
                 */
                return equal(value[1], value[2], type, previousValue);
            }
            else {
                /**
                 * Single Column
                 * type [eq|gt|lt] 	=> value[0]
                 * column			=> _column
                 * value		    => value[1]
                 */
                return equal(column, value[1], type, previousValue)
            }

        };

        let filterValues = "";
        for (let i = 0; i < values.length; i++) {
            filterValues += forEachValue(values[i]);
            previousValue = true;
        }

        return filterValues;
    },

    /**
     * Creates the Filter Query based on filter and the provider and type of service
     * 
     * @param {object[]} filters 
     * @param {object} currentCase 
     */
    getQueryAsXML: function (filters, currentCase) {
        let filterCount = 0;
        let queryString = "";

        for (let i = 0; i < filters.length; i++) {
            const filter = filters[i];
            queryString += this.getValuesForXML(currentCase, filter.column, filter.values);
            ++filterCount;
        }

        if (queryString && filterCount > 1) {
            queryString = currentCase.and.replace(this.regex.filter, queryString);
        }

        return currentCase.syntax.replace(this.regex.syntax, queryString)
    },

    getValuesForXML(currentCase, column, values) {
        const that = this;
        let filterValues = "";

        function between(column, min, max, type) {
            return that.setBetweenString(column, min, max, type, currentCase);
        };

        function equal(column, value, type) {
            return that.setEqualString(column, value, type, currentCase);
        };

        function forEachValue(value) {
            if (value[0] === "bt") {
                /**
                 * Time Filter (Single Column)
                 * type [bt] 		=> value[0]
                 * column			=> column
                 * min 			    => value[1]
                 * max				=> value[2]
                 */
                return between(column, value[1], value[2], value[0]);
            } else if (value.length === 3 && !column) {
                /** 
                 * Multiple (different) Columns
                 * type [eq|gt|lt] 	=> value[0]
                 * column			=> value[1]
                 * value		    => value[2]
                 */
                return equal(value[1], value[2], value[0]);
            } else {
                /**
                 * Single Column
                 * type [eq|gt|lt] 	=> value[0]
                 * column			=> column
                 * value		    => value[1]
                 */
                return equal(column, value[1], value[0]);
            }
        };

        for (let i = 0; i < values.length; i++) {
            filterValues += forEachValue(values[i]);
        }

        var template = this.setOperator('or', filterValues, values.length > 1, currentCase);

        return template;
    },

    setOperator: function (operator, string, condition, currentCase) {
        if (!condition) return string;
        return currentCase[operator].replace(
            this.regex.filter,
            string,
        );
    },

    getString(column, type, currentCase) {
        return currentCase[type].replace(
            this.regex.column,
            column,
        );
    },

    setEqualString: function (column, value, type, currentCase) {
        const val = currentCase._typeof[typeof value];
        const string = this.getString(column, type, currentCase);

        return string.replace(
            this.regex.value,
            val.replace(
                this.regex.value,
                value,
            )
        );
    },

    setBetweenString: function (column, min, max, type, currentCase) {
        const val = currentCase._typeof[typeof min];

        return this.getString(column, type, currentCase).replace(
            this.regex.min,
            val.replace(
                this.regex.value,
                min
            )
        ).replace(
            this.regex.max,
            val.replace(
                this.regex.value,
                max,
            )
        );
    }

}

export { FilterString };